import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { formatMoney }  from '../../utils/formatting.js';
import { showConfirm, showAlert } from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';

const TIER_LABELS = {
  junior: 'Junior',
  senior: 'Senior',
  expert: 'Expert'
};

const TIER_CLASSES = {
  junior: 'tier-junior',
  senior: 'tier-senior',
  expert: 'tier-expert'
};

function isRouteDraft(route) {
  return !route.shipId || !route.managerId || route.stops.length < 2;
}

export class RoutesPanel {
  constructor(uiManager) {
    this.ui = uiManager;
  }

  render() {
    const container = document.querySelector('.panel-content');
    const routes = [...GameState.tradeRoutes.values()];
    const managers = [...GameState.routeManagers.values()];
    const warehouses = [...GameState.warehouses.values()];

    const totalSalary = managers.reduce((sum, m) => sum + m.dailySalary, 0);
    const totalMaintenance = warehouses.length; // count for display

    // Header bar
    let html = `
      <div class="routes-header">
        <div class="routes-header-actions">
          <button class="btn btn-sm btn-primary" id="btn-new-route">New Route</button>
          <button class="btn btn-sm" id="btn-open-staff">Hire Staff</button>
        </div>
        <div class="routes-header-stats">
          <span>${managers.length} manager${managers.length !== 1 ? 's' : ''}, ${formatMoney(totalSalary)}/day salary</span>
          <span>${warehouses.length} warehouse${warehouses.length !== 1 ? 's' : ''}</span>
        </div>
      </div>`;

    // Route cards
    if (routes.length === 0) {
      html += '<p class="panel-empty">No trade routes created yet. Create a route to automate shipping.</p>';
    } else {
      html += '<div class="routes-list">';
      for (const route of routes) {
        const ship = GameState.getShip(route.shipId);
        const manager = GameState.routeManagers.get(route.managerId);
        const profit = route.stats.totalRevenue - route.stats.totalExpenses;
        const profitClass = profit >= 0 ? 'cargo-profit' : 'cargo-loss';
        const currentStop = route.getCurrentStop();
        const currentPort = currentStop ? GameState.getPort(currentStop.portId) : null;
        const draft = isRouteDraft(route);

        const statusLabel = draft ? 'Draft' : route.enabled ? 'Active' : 'Paused';
        const cardClass = draft ? 'route-draft' : route.enabled ? '' : 'route-disabled';

        html += `<div class="route-card ${cardClass}">
          <div class="route-card-header">
            <strong>${route.name}</strong>
            ${draft ? '<span class="route-draft-badge">Draft</span>' : ''}
            <label class="route-toggle-label">
              <input type="checkbox" class="route-enable-toggle" data-route-toggle="${route.id}" ${route.enabled ? 'checked' : ''} ${draft ? 'disabled' : ''}>
              ${statusLabel}
            </label>
          </div>
          <div class="route-card-body">
            <span>Ship: ${ship?.name || '—'}</span>
            <span>Manager: ${manager ? `${manager.name}` : '—'}
              ${manager ? `<span class="tier-badge ${TIER_CLASSES[manager.skillTier]}">${TIER_LABELS[manager.skillTier]}</span>` : ''}
            </span>
            ${!draft ? `<span>Stop: ${currentPort?.name || '?'} (${(route.currentStopIndex + 1)}/${route.stops.length})</span>` : `<span>${route.stops.length} stop${route.stops.length !== 1 ? 's' : ''}</span>`}
            ${!draft ? `<span>Loops: ${route.stats.completedLoops}</span>` : ''}
            ${!draft ? `<span class="${profitClass}">P&L: ${formatMoney(profit)}</span>` : ''}
          </div>
          <div class="route-card-actions">
            <button class="btn btn-sm" data-edit-route="${route.id}">Edit</button>
            <button class="btn btn-sm btn-danger" data-delete-route="${route.id}">Delete</button>
          </div>
        </div>`;
      }
      html += '</div>';
    }

    container.innerHTML = html;

    // Bind events
    document.getElementById('btn-new-route')?.addEventListener('click', () => {
      EventBus.emit('ui:routeEditorNew');
    });

    document.getElementById('btn-open-staff')?.addEventListener('click', () => {
      EventBus.emit('ui:staffOpen');
    });

    container.querySelectorAll('[data-route-toggle]').forEach(cb => {
      cb.addEventListener('change', () => {
        const routeId = cb.dataset.routeToggle;
        const route = GameState.tradeRoutes.get(routeId);
        if (route) {
          if (cb.checked && isRouteDraft(route)) {
            cb.checked = false;
            showAlert('Complete this route before enabling it (needs ship, manager, and at least 2 stops).');
            return;
          }
          // Send update intent to server
          serverIntent('route/update', { routeId, enabled: cb.checked }).then(result => {
            if (result.success) {
              route.enabled = cb.checked; // optimistic local update
              this.render();
            }
          });
        }
      });
    });

    container.querySelectorAll('[data-edit-route]').forEach(btn => {
      btn.addEventListener('click', () => {
        const route = GameState.tradeRoutes.get(btn.dataset.editRoute);
        if (route) EventBus.emit('ui:routeEditorOpen', { route });
      });
    });

    container.querySelectorAll('[data-delete-route]').forEach(btn => {
      btn.addEventListener('click', () => {
        const routeId = btn.dataset.deleteRoute;
        const route = GameState.tradeRoutes.get(routeId);
        if (route) {
          showConfirm(`Delete route "${route.name}"?`, { title: 'Delete Route', confirmLabel: 'Delete' }).then(ok => {
            if (!ok) return;
            serverIntent('route/delete', { routeId }).then(result => {
              if (result.success) {
                // Optimistic local update
                const ship = GameState.getShip(route.shipId);
                if (ship) ship.assignedRouteId = null;
                const mgr = GameState.routeManagers.get(route.managerId);
                if (mgr) mgr.assignedRouteId = null;
                GameState.tradeRoutes.delete(routeId);
                this.render();
              }
            });
          });
        }
      });
    });
  }
}
